import { useEffect, useState } from "react";
// ====== Config (1 kg) ======


// Z2/Z4 reales (tus datos) y Z3 estimado entre Z2 y Z4
const SHIPPING_TABLE = {
  Z2: {
    domicilio: { expreso: 10606, clasico: 7715 },
    sucursal:  { expreso: 6390,  clasico: 4644 },
  },
  Z3: {
    domicilio: { expreso: 13005, clasico: 8060 },
    sucursal:  { expreso: 7880,  clasico: 4878 },
  },
  Z4: {
    domicilio: { expreso: 15403, clasico: 8404 },
    sucursal:  { expreso: 9369,  clasico: 5111 },
  },
};

const formatAr = (n) =>
  n == null
    ? "—"
    : Number(n).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// === CAMBIO: normalización y mapeo CABA ===
const norm = (s) =>
  String(s || "")
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const zoneFromProvincia = (prov, cp) => {
  if (!prov) return null;

  // Gratis por regla de origen


  const p = norm(prov);

  // alias CABA
  const isCABA = p === "CABA" || p === "CIUDAD AUTONOMA DE BUENOS AIRES" || p === "CAPITAL FEDERAL";

  const z2 = new Set(["BUENOS AIRES","CORDOBA","ENTRE RIOS","LA PAMPA","SANTA FE"]);
  const z4 = new Set(["CHUBUT","JUJUY","MISIONES","SANTA CRUZ","TIERRA DEL FUEGO"]);
  const z3 = new Set([
    "CATAMARCA","CHACO","CORRIENTES","FORMOSA","LA RIOJA","MENDOZA",
    "NEUQUEN","RIO NEGRO","SAN JUAN","SAN LUIS","SANTIAGO DEL ESTERO","TUCUMAN",
  ]);

  if (isCABA) return "Z2";
  if (z2.has(p)) return "Z2";
  if (z3.has(p)) return "Z3";
  if (z4.has(p)) return "Z4";
  return null;
};

const estimateShipping = ({ provincia, cp, entrega, servicio }) => {
  if (!provincia || !cp || !entrega || !servicio) return null;
  const zona = zoneFromProvincia(provincia, cp);
  if (!zona || zona === "Z1") return 0;
  const t = SHIPPING_TABLE[zona];
  const val = t?.[entrega]?.[servicio];
  return typeof val === "number" ? val : null;
};

// === Helpers ETA ===
const addBusinessDays = (date, days) => {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay(); // 0 dom - 6 sáb
    if (day !== 0 && day !== 6) added++;
  }
  return d;
};
const fmtDateShort = (d) =>
  d.toLocaleDateString("es-AR", { weekday: "short", day: "numeric", month: "short" }).replace(/\./g, "");
const etaRangeFor = (servicio) =>
  servicio === "expreso" ? { min: 1, max: 3 } : { min: 3, max: 6 };

// === Spinner ===
const Spinner = () => (
  <span
    className="inline-block w-4 h-4 align-[-2px] border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin"
    aria-label="Cargando"
  />
);

/**
 * Props:
 * - provincia, cp (strings)            // === CAMBIO: localidad ya NO es requerida
 * - metodoEntrega: "domicilio" | "sucursal"
 * - onMetodoEntregaChange(next)
 * - servicio: "clasico" | "expreso"
 * - onServicioChange(next)
 * - onPrecioChange(number|null)
 *
 * Nota: el spinner SOLO aparece cuando cambia provincia/cp.
 */
export default function ShippingEstimator({
  provincia,
  cp,
  metodoEntrega,
  onMetodoEntregaChange,
  servicio,
  onServicioChange,
  onPrecioChange,
}) {
  // Para DOMICILIO pedimos provincia + CP (como tenías)
  const canQuote = !!(provincia && String(cp).trim().length >= 4);

  const [loading, setLoading] = useState(false);
  const [precio, setPrecio] = useState(null);

  // === Fechas (usamos tus helpers addBusinessDays y fmtDateShort) ===
  const hoy = new Date();

  // SUCURSAL: 2–5 días hábiles -> mostrar FECHAS concretas
  const etaTextSucursal = () => {
    const a = addBusinessDays(hoy, 2);
    const b = addBusinessDays(hoy, 5);
    return `${fmtDateShort(a)} – ${fmtDateShort(b)}`;
  };

  // DOMICILIO: usamos tu etaRangeFor(srv) para fechas concretas
  const etaText = (srv) => {
    const { min, max } = etaRangeFor(srv);
    const a = addBusinessDays(hoy, min);
    const b = addBusinessDays(hoy, max);
    return `${fmtDateShort(a)} – ${fmtDateShort(b)}`;
  };

  const formatAr = (n) =>
    n == null
      ? "—"
      : Number(n).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const priceOf = (entrega, srv) =>
    loading || !canQuote ? null : estimateShipping({ provincia, cp, entrega, servicio: srv });

  const isSelectedService = (srv) => servicio === srv;

  const chooseService = (srv) => {
    onServicioChange?.(srv);
    if (metodoEntrega === "sucursal") {
      setPrecio(0);
      onPrecioChange?.(0);
      return;
    }
    if (canQuote) {
      const p = estimateShipping({ provincia, cp, entrega: metodoEntrega, servicio: srv });
      setPrecio(p);
      onPrecioChange?.(p);
    }
  };

  // Reacción a cambios de prov/CP o método
  useEffect(() => {
    if (metodoEntrega === "sucursal") {
      setLoading(false);
      setPrecio(0);
      onPrecioChange?.(0);
      return;
    }
    if (!canQuote) {
      setPrecio(null);
      setLoading(false);
      onPrecioChange?.(null);
      return;
    }
    setLoading(true);
    const delayMs = Math.floor(3000 + Math.random() * 2000); // 3–5s
    const timer = setTimeout(() => {
      const p = estimateShipping({
        provincia,
        cp,
        entrega: "domicilio",
        servicio: servicio || "clasico",
      });
      setPrecio(p);
      onPrecioChange?.(p);
      setLoading(false);
    }, delayMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provincia, cp, metodoEntrega]);

  // Reacción a cambio de servicio (recotiza solo domicilio)
  useEffect(() => {
    if (metodoEntrega === "sucursal") {
      setPrecio(0);
      onPrecioChange?.(0);
      return;
    }
    if (!canQuote) return;
    const p = estimateShipping({
      provincia,
      cp,
      entrega: "domicilio",
      servicio: servicio || "clasico",
    });
    setPrecio(p);
    onPrecioChange?.(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicio]);

  return (
    <div className="border rounded-xl p-3 bg-white">
      <div className="text-sm font-semibold mb-2">Opciones de envío</div>

      {/* Botones: Domicilio / Sucursal */}
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={() => onMetodoEntregaChange?.("domicilio")}
          className={`px-3 py-1.5 rounded-md border text-sm ${
            metodoEntrega === "domicilio"
              ? "bg-black text-white border-black"
              : "bg-white text-gray-900 border-gray-300"
          }`}
        >
          Domicilio
        </button>
        <button
          type="button"
          onClick={() => onMetodoEntregaChange?.("sucursal")}
          className={`px-3 py-1.5 rounded-md border text-sm ${
            metodoEntrega === "sucursal"
              ? "bg-black text-white border-black"
              : "bg-white text-gray-900 border-gray-300"
          }`}
        >
          Sucursal
        </button>
      </div>

      {/* Resultado / opciones */}
      {metodoEntrega === "sucursal" ? (
        // ===== SUCURSAL: SOLO CLÁSICO, GRATIS, ETA con fechas (2–5 hábiles) =====
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 rounded-md border bg-gray-50 border-gray-200">
            <div className="min-w-0">
              <div className="font-medium text-sm">Clásico</div>
              <div className="text-[12px] text-gray-600">
                Llega entre <span className="font-medium">{etaTextSucursal()}</span>
              </div>
            </div>
            <div className="text-sm font-semibold text-green-700">Gratis</div>
          </div>
        </div>
      ) : !canQuote ? (
        <p className="text-sm text-gray-600">
          Ingresá <span className="font-medium">provincia</span> y{" "}
          <span className="font-medium">CP</span> para calcular el envío a domicilio.
        </p>
      ) : loading ? (
        <p className="text-sm text-gray-800">
          <Spinner /> <span className="ml-1">Calculando…</span>
        </p>
      ) : (
        // ===== DOMICILIO: precios estimados, ETA con fechas =====
        <div className="space-y-2">
          {/* Clásico */}
          <label
            className={`flex items-center justify-between p-2 rounded-md border cursor-pointer ${
              isSelectedService("clasico") ? "border-black bg-gray-50" : "border-gray-200"
            }`}
          >
            <div className="min-w-0">
              <div className="font-medium text-sm">Clásico</div>
              <div className="text-[12px] text-gray-600">
                Llega entre <span className="font-medium">{etaText("clasico")}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 pl-3">
              <div className="text-sm font-semibold">
                {priceOf("domicilio", "clasico") == null
                  ? "—"
                  : `$${formatAr(priceOf("domicilio", "clasico"))}`}
              </div>
              <input
                type="radio"
                name="shipping-service"
                checked={isSelectedService("clasico")}
                onChange={() => chooseService("clasico")}
              />
            </div>
          </label>

          {/* Expreso (solo DOMICILIO) */}
          <label
            className={`flex items-center justify-between p-2 rounded-md border cursor-pointer ${
              isSelectedService("expreso") ? "border-black bg-gray-50" : "border-gray-200"
            }`}
          >
            <div className="min-w-0">
              <div className="font-medium text-sm">Expreso</div>
              <div className="text-[12px] text-gray-600">
                Llega entre <span className="font-medium">{etaText("expreso")}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 pl-3">
              <div className="text-sm font-semibold">
                {priceOf("domicilio", "expreso") == null
                  ? "—"
                  : `$${formatAr(priceOf("domicilio", "expreso"))}`}
              </div>
              <input
                type="radio"
                name="shipping-service"
                checked={isSelectedService("expreso")}
                onChange={() => chooseService("expreso")}
              />
            </div>
          </label>
        </div>
      )}
    </div>
  );
}